<template>
  <div class="staff-directory">
    <!-- Staff List Container -->
    <div class="staff-list-wrapper">
      <!-- Loading State -->
      <div v-if="get_staff_loading" class="loading-section">
        <div class="modern-loader">
          <div class="loader-rings">
            <div class="loader-ring"></div>
            <div class="loader-ring"></div>
            <div class="loader-ring"></div>
          </div>
          <div class="loader-text">Loading...</div>
        </div>
      </div>

      <!-- Staff Directory Content -->
      <div v-if="!get_staff_loading" class="staff-content">
        <!-- Header Section -->
        <div class="staff-header">
          <div class="header-content">
            <div class="header-title-section">
              <h2 class="directory-title">
                <!-- <i class="el-icon-user-solid"></i> -->
                Staff Directory
              </h2>
              <p class="directory-subtitle">Manage and search staff information</p>
            </div>
            
            <div class="search-controls">
              <div class="search-input-container">
                <el-input 
                  class="search-input" 
                  :placeholder="'Search by ' + form.pilihan" 
                  size="medium" 
                  v-model="form.search"
                  clearable
                >
                  <i slot="prefix" class="el-input__icon el-icon-search"></i>
                </el-input>
              </div>
              
              <div class="filter-container">
                <el-select 
                  v-model="form.pilihan" 
                  placeholder="Filter By" 
                  size="medium" 
                  @change="update_search"
                  class="filter-select"
                >
                  <el-option 
                    v-for="item in options" 
                    :key="item.value" 
                    :label="item.label" 
                    :value="item.value"
                  ></el-option>
                </el-select>
              </div>
            </div>
            
            <div class="stats-section">
              <div class="staff-counter">
                <div class="counter-icon">
                  <i class="el-icon-s-custom"></i>
                </div>
                <div class="counter-info">
                  <span class="counter-number">{{ get_staff.length || 0 }}</span>
                  <span class="counter-label">Staff Members</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Staff List -->
        <div class="staff-list-container">
          <!-- View Toggle -->
          <div class="view-toggle-section">
            <div class="view-options">
              <button 
                class="view-toggle-btn"
                :class="{ active: viewMode === 'grid' }"
                @click="viewMode = 'grid'"
                title="Grid View"
              >
                <i class="el-icon-menu"></i>
                <span>Grid</span>
              </button>
              <button 
                class="view-toggle-btn"
                :class="{ active: viewMode === 'list' }"
                @click="viewMode = 'list'"
                title="List View"
              >
                <i class="el-icon-document"></i>
                <span>List</span>
              </button>
            </div>
            
            <div class="sort-options">
              <el-select 
                v-model="sortBy" 
                placeholder="Sort By" 
                size="small"
                @change="sortStaff"
                class="sort-select"
              >
                <el-option label="Name A-Z" value="name-asc"></el-option>
                <el-option label="Name Z-A" value="name-desc"></el-option>
                <el-option label="Age ↑" value="age-asc"></el-option>
                <el-option label="Age ↓" value="age-desc"></el-option>
                <el-option label="Position" value="position"></el-option>
              </el-select>
            </div>
          </div>

          <!-- Grid View -->
          <div v-if="viewMode === 'grid'" class="staff-grid">
            <div 
              v-for="(item, index) in sortedStaff" 
              :key="index" 
              class="staff-card modern-card"
              :style="{ animationDelay: (index * 0.05) + 's' }"
            >
              <!-- Card Header with Badge -->
              <div class="card-header">
                <div class="department-badge">
                  <i class="el-icon-office-building"></i>
                  <span>{{ item.jabatan ? item.jabatan.split(' ')[0] : 'Dept' }}</span>
                </div>
                <div class="card-menu">
                  <button class="menu-btn" @click="toggleCardMenu(index)">
                    <i class="el-icon-more"></i>
                  </button>
                  <div v-if="activeCardMenu === index" class="card-dropdown">
                    <a href="#" class="dropdown-item">
                      <i class="el-icon-view"></i> View Profile
                    </a>
                    <a href="#" class="dropdown-item">
                      <i class="el-icon-edit"></i> Edit Info
                    </a>
                    <a href="#" class="dropdown-item">
                      <i class="el-icon-message"></i> Send Message
                    </a>
                  </div>
                </div>
              </div>
              
              <!-- Avatar Section -->
              <div class="staff-avatar-section">
                <div class="avatar-container">
                  <div class="avatar-ring"></div>
                  <img 
                    :src="getAvatarSrc(item.jenisKelamin)" 
                    :alt="item.nama"
                    class="staff-avatar"
                  >
                  <div class="avatar-status" :class="getGenderStatusClass(item.jenisKelamin)">
                    <i :class="getGenderIcon(item.jenisKelamin)"></i>
                  </div>
                  <div class="online-indicator"></div>
                </div>
              </div>
              
              <!-- Staff Information -->
              <div class="staff-info-section">
                <div class="primary-info">
                  <h3 class="staff-name">{{ item.nama }}</h3>
                  <p class="staff-position">{{ item.jabatan || 'No Position Assigned' }}</p>
                </div>
                
                <div class="info-divider"></div>
                
                <div class="secondary-info">
                  <div class="info-row">
                    <div class="info-item">
                      <div class="info-icon">
                        <i class="el-icon-postcard"></i>
                      </div>
                      <div class="info-content">
                        <span class="info-label">ID</span>
                        <span class="info-value nip-value">{{ item.nip | format_nip }}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div class="info-row">
                    <div class="info-item">
                      <div class="info-icon">
                        <i class="el-icon-message"></i>
                      </div>
                      <div class="info-content">
                        <span class="info-label">Email</span>
                        <span class="info-value email-value">{{ item.username }}@lan.go.id</span>
                      </div>
                    </div>
                  </div>
                  
                  <div class="info-row">
                    <div class="info-item">
                      <div class="info-icon">
                        <i class="el-icon-date"></i>
                      </div>
                      <div class="info-content">
                        <span class="info-label">Age</span>
                        <span class="info-value age-text">{{ item.nip | format_age }} years old</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Card Footer -->
              <div class="card-footer">
                <div class="action-buttons">
                  <button class="action-btn primary-btn" title="View Profile">
                    <i class="el-icon-user"></i>
                    <span>Profile</span>
                  </button>
                  <button class="action-btn secondary-btn" title="Contact">
                    <i class="el-icon-chat-dot-square"></i>
                    <span>Contact</span>
                  </button>
                </div>
                
                <div class="staff-rating">
                  <div class="rating-stars">
                    <i class="el-icon-star-on"></i>
                    <i class="el-icon-star-on"></i>
                    <i class="el-icon-star-on"></i>
                    <i class="el-icon-star-on"></i>
                    <i class="el-icon-star-off"></i>
                  </div>
                  <span class="rating-text">4.0</span>
                </div>
              </div>
            </div>
          </div>

          <!-- List View -->
          <div v-if="viewMode === 'list'" class="staff-list-view">
            <div class="list-header">
              <div class="list-header-item avatar-col">Photo</div>
              <div class="list-header-item name-col">Name</div>
              <div class="list-header-item position-col">Position</div>
              <div class="list-header-item nip-col">Employee ID</div>
              <div class="list-header-item email-col">Email</div>
              <div class="list-header-item age-col">Age</div>
              <div class="list-header-item actions-col">Actions</div>
            </div>
            
            <div class="list-body">
              <div 
                v-for="(item, index) in sortedStaff" 
                :key="index" 
                class="list-row"
                :style="{ animationDelay: (index * 0.03) + 's' }"
              >
                <div class="list-cell avatar-col">
                  <div class="list-avatar-container">
                    <img 
                      :src="getAvatarSrc(item.jenisKelamin)" 
                      :alt="item.nama"
                      class="list-avatar"
                    >
                    <div class="list-status" :class="getGenderStatusClass(item.jenisKelamin)"></div>
                  </div>
                </div>
                
                <div class="list-cell name-col">
                  <div class="name-info">
                    <span class="list-name">{{ item.nama }}</span>
                    <span class="list-id">#{{ index + 1 }}</span>
                  </div>
                </div>
                
                <div class="list-cell position-col">
                  <div class="position-badge">
                    <i class="el-icon-suitcase"></i>
                    <span>{{ item.jabatan || 'No Position' }}</span>
                  </div>
                </div>
                
                <div class="list-cell nip-col">
                  <code class="nip-code">{{ item.nip | format_nip }}</code>
                </div>
                
                <div class="list-cell email-col">
                  <a :href="'mailto:' + item.username + '@lan.go.id'" class="email-link">
                    <i class="el-icon-message"></i>
                    {{ item.username }}@lan.go.id
                  </a>
                </div>
                
                <div class="list-cell age-col">
                  <div class="age-indicator">
                    <span class="age-number">{{ item.nip | format_age }}</span>
                    <span class="age-label">years</span>
                  </div>
                </div>
                
                <div class="list-cell actions-col">
                  <div class="list-actions">
                    <button class="list-action-btn view-btn" title="View">
                      <i class="el-icon-view"></i>
                    </button>
                    <button class="list-action-btn edit-btn" title="Edit">
                      <i class="el-icon-edit"></i>
                    </button>
                    <button class="list-action-btn more-btn" title="More">
                      <i class="el-icon-more"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Empty State -->
          <div v-if="get_staff.length === 0" class="empty-state">
            <div class="empty-icon">
              <i class="el-icon-user"></i>
            </div>
            <h3 class="empty-title">No Staff Found</h3>
            <p class="empty-description">
              Try adjusting your search criteria or filter options to find staff members.
            </p>
            <button class="refresh-btn" @click="refreshData">
              <i class="el-icon-refresh"></i>
              Refresh Data
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    'deviceType': String
  },
  data() {
    return {
      form: {search: null, pilihan: "NIP"},
      options: [{
          value: 'NIP',
          label: 'NIP'
        }, {
          value: 'Nama',
          label: 'Nama'
        }, {
          value: 'Jabatan',
          label: 'Jabatan'
        }],
      viewMode: 'grid', // 'grid' or 'list'
      sortBy: 'name-asc',
      activeCardMenu: null
    }
  },
  filters: {
    format_nip: function(value) {
      return value.substring(0, 8)+" "+value.substring(8, 14)+" "+value.substring(14, 15)+" "+value.substring(15, 18);
    },
    format_age: function(value) {
      const numStr = value.substring(0, 8);

      const year = numStr.substring(0, 4);
      const month = numStr.substring(4, 6);
      const day = numStr.substring(6, 8);

      const birthDate = `${year}-${month}-${day}`;

      const today = new Date();
      const birth = new Date(birthDate);
      
      let age = today.getFullYear() - birth.getFullYear();
      const monthDifference = today.getMonth() - birth.getMonth();

      if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birth.getDate())) {
        age--;
      }

      return age;
    }
  },
  computed: {
    get_user() {
      return this.$store.state.user.user
    },
    get_staff() {
      if (this.form.search == null) {
        return this.$store.state.staff.staff
      } else if (this.form.pilihan == 'NIP') {
        return this.$store.state.staff.staff.filter(text => new RegExp(this.form.search).test(text.nip))
      } else if (this.form.pilihan == 'Nama') {
        return this.$store.state.staff.staff.filter(text => new RegExp(this.form.search).test(text.nama))
      } else if (this.form.pilihan == 'Jabatan') {
        return this.$store.state.staff.staff.filter(text => new RegExp(this.form.search).test(text.jabatan))
      } 

      return this.$store.state.staff.staff 
    },
    get_user_loading() {
      return this.$store.state.user.loading
    },
    get_staff_loading() {
      return this.$store.state.staff.loading
    },
    sortedStaff() {
      const staff = [...this.get_staff];
      
      switch (this.sortBy) {
        case 'name-asc':
          return staff.sort((a, b) => a.nama.localeCompare(b.nama));
        case 'name-desc':
          return staff.sort((a, b) => b.nama.localeCompare(a.nama));
        case 'age-asc':
          return staff.sort((a, b) => this.calculateAge(a.nip) - this.calculateAge(b.nip));
        case 'age-desc':
          return staff.sort((a, b) => this.calculateAge(b.nip) - this.calculateAge(a.nip));
        case 'position':
          return staff.sort((a, b) => (a.jabatan || '').localeCompare(b.jabatan || ''));
        default:
          return staff;
      }
    }
  },
  created() {
    this.$store.dispatch('user/get_user')
    this.$store.dispatch('staff/get_staff')
  },
  methods: {
    update_search: function() {
      this.form.search = null;
    },
    
    getAvatarSrc(gender) {
      if (gender === 'L') return require('@/assets/img/user_p.png');
      if (gender === 'P') return require('@/assets/img/user_w.png');
      return require('@/assets/img/user.png');
    },
    
    getGenderStatusClass(gender) {
      if (gender === 'L') return 'male-status';
      if (gender === 'P') return 'female-status';
      return 'unknown-status';
    },
    
    getGenderIcon(gender) {
      if (gender === 'L') return 'el-icon-male';
      if (gender === 'P') return 'el-icon-female';
      return 'el-icon-user-solid';
    },
    
    calculateAge(nip) {
      const numStr = nip.substring(0, 8);
      const year = numStr.substring(0, 4);
      const month = numStr.substring(4, 6);
      const day = numStr.substring(6, 8);
      const birthDate = `${year}-${month}-${day}`;
      const today = new Date();
      const birth = new Date(birthDate);
      let age = today.getFullYear() - birth.getFullYear();
      const monthDifference = today.getMonth() - birth.getMonth();
      if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birth.getDate())) {
        age--;
      }
      return age;
    },
    
    sortStaff() {
      // Sorting handled by computed property
    },
    
    toggleCardMenu(index) {
      this.activeCardMenu = this.activeCardMenu === index ? null : index;
    },
    
    refreshData() {
      this.$store.dispatch('staff/get_staff');
      this.form.search = null;
      this.form.pilihan = 'NIP';
      this.sortBy = 'name-asc';
      this.viewMode = 'grid';
    }
  },
}
</script>

<style scoped>
/* Main Container */
.staff-directory {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 30%, #f1f5f9 70%, #ffffff 100%);
  min-height: 100vh;
  padding: 2rem;
}

.staff-list-wrapper {
  max-width: 1400px;
  margin: 0 auto;
}

/* Loading State */
.loading-section {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
}

.modern-loader {
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 3rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
}

.loader-rings {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
}

.loader-ring {
  width: 60px;
  height: 60px;
  border: 4px solid rgba(59, 130, 246, 0.1);
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  position: absolute;
}

.loader-ring:nth-child(1) {
  width: 60px;
  height: 60px;
  animation-duration: 1s;
}

.loader-ring:nth-child(2) {
  width: 80px;
  height: 80px;
  animation-duration: 1.5s;
  animation-direction: reverse;
}

.loader-ring:nth-child(3) {
  width: 100px;
  height: 100px;
  animation-duration: 2s;
}

.loader-text {
  color: #3b82f6;
  font-size: 1.1rem;
  font-weight: 600;
  letter-spacing: 0.5px;
  margin-top: 2rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Staff Content */
.staff-content {
  animation: fadeInUp 0.8s ease forwards;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Header Section */
.staff-header {
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  border-radius: 15px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.9);
  margin-bottom: 2rem;
  overflow: hidden;
}

.header-content {
  padding: 1.5rem;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 2rem;
  align-items: center;
}

.header-title-section {
  text-align: left;
}

.directory-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.directory-title i {
  color: #3b82f6;
  font-size: 1.5rem;
}

.directory-subtitle {
  color: #64748b;
  margin: 0;
  font-size: 1rem;
  font-weight: 500;
}

.search-controls {
  display: flex;
  gap: 1rem;
  min-width: 400px;
  justify-content: center;
}

.search-input-container {
  flex: 1;
  max-width: 250px;
}

.search-input >>> .el-input__inner {
  background: rgba(255, 255, 255, 0.95);
  border: 2px solid rgba(59, 130, 246, 0.1);
  border-radius: 10px;
  padding: 12px 45px 12px 45px;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
}

.search-input >>> .el-input__inner:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
}

.search-input >>> .el-input__prefix {
  left: 15px;
  color: #3b82f6;
}

.filter-container {
  min-width: 130px;
}

.filter-select >>> .el-input__inner {
  background: rgba(255, 255, 255, 0.95);
  border: 2px solid rgba(59, 130, 246, 0.1);
  border-radius: 10px;
  padding: 12px 15px;
  font-size: 1rem;
  transition: all 0.3s ease;
  /* box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05); */
}

.filter-select >>> .el-input__inner:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1);
}

.stats-section {
  display: flex;
  justify-content: flex-end;
}

.staff-counter {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  padding: 1rem 1.5rem;
  border-radius: 14px;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.3);
}

.counter-icon {
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
}

.counter-info {
  display: flex;
  flex-direction: column;
}

.counter-number {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1;
}

.counter-label {
  font-size: 0.875rem;
  opacity: 0.9;
  font-weight: 500;
}

/* Staff List Container */
.staff-list-container {
  padding: 1rem 0;
}

/* View Toggle Section */
.view-toggle-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding: 0.5rem;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  border-radius: 15px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.9);
}

.view-options {
  display: flex;
  /* gap: 0.5rem; */
  /* background: rgba(241, 245, 249, 0.8); */
  /* padding: 0.5rem; */
  border-radius: 16px;
}

.view-toggle-btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 12px;
  background: transparent;
  color: #64748b;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
}

.view-toggle-btn:hover {
  color: #3b82f6;
  background: rgba(59, 130, 246, 0.1);
}

.view-toggle-btn.active {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
}

.sort-options {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.sort-select >>> .el-input__inner {
  background: rgba(255, 255, 255, 0.95);
  border: 2px solid rgba(59, 130, 246, 0.1);
  border-radius: 10px;
  /* padding: 8px 12px; */
  /* font-size: 1rem; */
  min-width: 140px;
}

.staff-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 1.5rem;
}

/* Staff Card */
.staff-card {
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  padding: 1.5rem;
  border: 2px solid rgba(59, 130, 246, 0.08);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  animation: slideInUp 0.6s ease forwards;
  opacity: 0;
  transform: translateY(20px);
}

.staff-card.modern-card {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.staff-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(59, 130, 246, 0.05), transparent);
  transition: left 0.6s ease;
}

.staff-card:hover::before {
  left: 100%;
}

.staff-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 50px rgba(59, 130, 246, 0.15);
  border-color: rgba(59, 130, 246, 0.2);
}

@keyframes slideInUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Card Header */
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.department-badge {
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  color: #4b5563;
  padding: 0.5rem 1rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.card-menu {
  position: relative;
}

.menu-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: rgba(107, 114, 128, 0.1);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #6b7280;
}

.menu-btn:hover {
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
}

.card-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(59, 130, 246, 0.1);
  min-width: 160px;
  z-index: 10;
  overflow: hidden;
  animation: dropdownSlide 0.3s ease forwards;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  color: #4b5563;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.3s ease;
}

.dropdown-item:hover {
  background: rgba(59, 130, 246, 0.05);
  color: #3b82f6;
}

@keyframes dropdownSlide {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Avatar Section */
.staff-avatar-section {
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
}

.avatar-container {
  position: relative;
  width: 100px;
  height: 100px;
}

.avatar-ring {
  position: absolute;
  top: -4px;
  left: -4px;
  right: -4px;
  bottom: -4px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 50%, #06b6d4 100%);
  animation: rotateRing 3s linear infinite;
  opacity: 0.8;
}

@keyframes rotateRing {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.staff-avatar {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  border: 4px solid white;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  position: relative;
  z-index: 2;
}

.staff-card:hover .staff-avatar {
  transform: scale(1.05);
  box-shadow: 0 12px 35px rgba(0, 0, 0, 0.2);
}

.avatar-status {
  position: absolute;
  bottom: 8px;
  right: 8px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 3px solid white;
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  color: white;
  z-index: 3;
}

.online-indicator {
  position: absolute;
  top: 5px;
  right: 5px;
  width: 16px;
  height: 16px;
  background: #10b981;
  border-radius: 50%;
  border: 3px solid white;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
  z-index: 3;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

.avatar-status.male-status {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
}

.avatar-status.female-status {
  background: linear-gradient(135deg, #ec4899 0%, #be185d 100%);
}

.avatar-status.unknown-status {
  background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
}

/* Staff Info Section */
.staff-info-section {
  flex: 1;
}

.primary-info {
  text-align: center;
  margin-bottom: 1.5rem;
}

.staff-name {
  font-size: 1.4rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 0.5rem 0;
  line-height: 1.2;
  transition: color 0.3s ease;
}

.staff-card:hover .staff-name {
  color: #3b82f6;
}

.staff-position {
  font-size: 1rem;
  color: #64748b;
  margin: 0;
  font-weight: 500;
  padding: 0.5rem 1rem;
  background: rgba(59, 130, 246, 0.05);
  border-radius: 20px;
  display: inline-block;
}

.info-divider {
  height: 1px;
  background: linear-gradient(90deg, transparent, #e2e8f0, transparent);
  margin: 1.5rem 0;
}

.secondary-info {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.info-row {
  display: flex;
  align-items: center;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex: 1;
  padding: 0.75rem;
  background: rgba(248, 250, 252, 0.8);
  border-radius: 12px;
  transition: all 0.3s ease;
}

.info-item:hover {
  background: rgba(59, 130, 246, 0.05);
  transform: translateX(4px);
}

.info-icon {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1rem;
  flex-shrink: 0;
}

.info-content {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
  flex: 1;
}

.info-label {
  font-size: 0.75rem;
  color: #94a3b8;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: 0.9rem;
  color: #475569;
  font-weight: 600;
}

.nip-value {
  font-family: 'Courier New', monospace;
  /* background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%); */
  /* color: white; */
  color: #374151;
  /* padding: 0.4rem 0.8rem; */
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.email-value {
  color: #3b82f6;
  font-size: 0.85rem;
}

.age-text {
  color: #059669;
  font-weight: 600;
}

/* Card Footer */
.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 1rem;
  border-top: 1px solid rgba(226, 232, 240, 0.8);
}

.action-buttons {
  display: flex;
  gap: 0.75rem;
}

.action-btn {
  padding: 0.75rem 1.25rem;
  border: none;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.primary-btn {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
}

.primary-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(59, 130, 246, 0.4);
}

.secondary-btn {
  background: rgba(107, 114, 128, 0.1);
  color: #6b7280;
  border: 1px solid rgba(107, 114, 128, 0.2);
}

.secondary-btn:hover {
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
  border-color: rgba(59, 130, 246, 0.2);
  transform: translateY(-2px);
}

.staff-rating {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.rating-stars {
  display: flex;
  gap: 0.2rem;
  color: #fbbf24;
  font-size: 1rem;
}

.rating-text {
  font-size: 0.9rem;
  font-weight: 600;
  color: #6b7280;
}

/* List View */
.staff-list-view {
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.9);
  overflow: hidden;
}

.list-header {
  display: grid;
  grid-template-columns: 80px 1fr 200px 180px 250px 80px 120px;
  gap: 1rem;
  padding: 1.5rem 2rem;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-bottom: 2px solid rgba(59, 130, 246, 0.1);
}

.list-header-item {
  font-size: 0.85rem;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
}

.list-body {
  max-height: 600px;
  overflow-y: auto;
}

.list-row {
  display: grid;
  grid-template-columns: 80px 1fr 200px 180px 250px 80px 120px;
  gap: 1rem;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid rgba(226, 232, 240, 0.5);
  transition: all 0.3s ease;
  align-items: center;
  animation: slideInUp 0.6s ease forwards;
  opacity: 0;
  transform: translateY(20px);
}

.list-row:hover {
  background: rgba(59, 130, 246, 0.02);
  transform: translateX(4px);
}

.list-row:last-child {
  border-bottom: none;
}

.list-cell {
  display: flex;
  align-items: center;
}

/* List Avatar */
.list-avatar-container {
  position: relative;
  width: 50px;
  height: 50px;
}

.list-avatar {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.list-status {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 2px solid white;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

/* List Name */
.name-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.list-name {
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
}

.list-id {
  font-size: 0.8rem;
  color: #94a3b8;
  font-weight: 500;
}

/* List Position */
.position-badge {
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  max-width: 180px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* List NIP */
.nip-code {
  font-family: 'Courier New', monospace;
  background: linear-gradient(135deg, #1e293b 0%, #374151 100%);
  color: white;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.5px;
}

/* List Email */
.email-link {
  color: #3b82f6;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  max-width: 240px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.email-link:hover {
  background: rgba(59, 130, 246, 0.1);
  transform: translateX(2px);
}

/* List Age */
.age-indicator {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  text-align: center;
  font-weight: 600;
  box-shadow: 0 3px 10px rgba(16, 185, 129, 0.3);
}

.age-number {
  display: block;
  font-size: 1rem;
  line-height: 1;
}

.age-label {
  font-size: 0.7rem;
  opacity: 0.9;
}

/* List Actions */
.list-actions {
  display: flex;
  gap: 0.5rem;
}

.list-action-btn {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.9rem;
}

.list-action-btn.view-btn {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  box-shadow: 0 3px 10px rgba(59, 130, 246, 0.3);
}

.list-action-btn.edit-btn {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  box-shadow: 0 3px 10px rgba(245, 158, 11, 0.3);
}

.list-action-btn.more-btn {
  background: rgba(107, 114, 128, 0.1);
  color: #6b7280;
}

.list-action-btn:hover {
  transform: scale(1.1);
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  color: #64748b;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.9);
}

.empty-icon {
  font-size: 4rem;
  color: #cbd5e1;
  margin-bottom: 1.5rem;
}

.empty-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #475569;
  margin: 0 0 1rem 0;
}

.empty-description {
  font-size: 1rem;
  margin: 0 0 2rem 0;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
}

.refresh-btn {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 16px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.3);
}

.refresh-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(59, 130, 246, 0.4);
}

/* Responsive Design */
@media (max-width: 1200px) {
  .staff-grid {
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  }
  
  .header-content {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    text-align: center;
  }
  
  .search-controls {
    justify-content: center;
    min-width: auto;
  }
  
  .list-header,
  .list-row {
    grid-template-columns: 60px 1fr 150px 140px 200px 60px 100px;
    gap: 0.75rem;
    padding: 1rem 1.5rem;
  }
}

@media (max-width: 768px) {
  .staff-directory {
    padding: 1rem;
  }
  
  .staff-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .staff-card {
    padding: 1.25rem;
    border-radius: 16px;
  }
  
  .header-content {
    padding: 1.5rem;
  }
  
  .directory-title {
    font-size: 1.5rem;
  }
  
  .search-controls {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .search-input-container,
  .filter-container {
    max-width: none;
  }
  
  .staff-counter {
    padding: 0.875rem 1.25rem;
  }
  
  .avatar-container {
    width: 80px;
    height: 80px;
  }
  
  .view-toggle-section {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
  
  .view-options {
    justify-content: center;
  }
  
  .sort-options {
    justify-content: center;
  }
  
  /* List View Mobile */
  .list-header {
    display: none;
  }
  
  .list-row {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1.5rem;
    border-radius: 16px;
    margin-bottom: 1rem;
    background: white;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }
  
  .list-cell {
    width: 100%;
  }
  
  .list-avatar-container {
    width: 60px;
    height: 60px;
    margin: 0 auto;
  }
  
  .name-info {
    text-align: center;
  }
  
  .list-actions {
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .staff-directory {
    padding: 0.5rem;
  }
  
  .staff-card {
    padding: 1rem;
  }
  
  .avatar-container {
    width: 70px;
    height: 70px;
  }
  
  .staff-name {
    font-size: 1.2rem;
  }
  
  .directory-title {
    font-size: 1.25rem;
  }
  
  .action-buttons {
    flex-direction: column;
    gap: 0.5rem;
    width: 100%;
  }
  
  .action-btn {
    justify-content: center;
    width: 100%;
  }
  
  .card-footer {
    flex-direction: column;
    gap: 1rem;
    align-items: center;
  }
  
  .view-toggle-btn {
    flex: 1;
    justify-content: center;
  }
  
  .info-item {
    padding: 0.5rem;
    gap: 0.75rem;
  }
  
  .info-icon {
    width: 32px;
    height: 32px;
    font-size: 0.9rem;
  }
}

/* Element UI Overrides */
.el-select-dropdown {
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.1);
}

.el-select-dropdown__item {
  /* padding: 12px 20px; */
  transition: all 0.3s ease;
}

.el-select-dropdown__item:hover {
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
}

.el-select-dropdown__item.selected {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
  font-weight: 600;
}

/* Animation delays for staggered effect */
.staff-card:nth-child(1) { animation-delay: 0.1s; }
.staff-card:nth-child(2) { animation-delay: 0.15s; }
.staff-card:nth-child(3) { animation-delay: 0.2s; }
.staff-card:nth-child(4) { animation-delay: 0.25s; }
.staff-card:nth-child(5) { animation-delay: 0.3s; }
.staff-card:nth-child(6) { animation-delay: 0.35s; }
.staff-card:nth-child(n+7) { animation-delay: 0.4s; }
</style>